import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Both string and numeric SDK-enum forms are keyed here, even though the
// sync now writes normalized strings ('enabled'/'paused'/'removed') after
// the normalizeCampaignStatus() fix in backend/routes/googleAds.js — the
// numeric keys are defensive against any stale row that pre-dates the fix.
const STATUS_COLORS = {
  active:   { bg: '#f0fdf4', color: '#166534', label: 'Active' },
  enabled:  { bg: '#f0fdf4', color: '#166534', label: 'Active' },
  '2':      { bg: '#f0fdf4', color: '#166534', label: 'Active' },
  paused:   { bg: '#fefce8', color: '#854d0e', label: 'Paused' },
  '3':      { bg: '#fefce8', color: '#854d0e', label: 'Paused' },
  removed:  { bg: '#fef2f2', color: '#991b1b', label: 'Removed' },
  '4':      { bg: '#fef2f2', color: '#991b1b', label: 'Removed' },
};

// Platform sub-tabs — Google is live today; Meta + LinkedIn are scaffolded
// behind a "Soon" badge so the IA reflects the multi-platform plan.
const PLATFORMS = [
  { value: 'google',   label: 'Google Ads' },
  { value: 'meta',     label: 'Meta Ads',     comingSoon: true },
  { value: 'linkedin', label: 'LinkedIn Ads', comingSoon: true },
];

// KPI tooltip copy — title + plain-English description + benchmark.
// lowerIsBetter flips the delta colorization (a ↓ in CPA is good, ↓ in
// conversions is bad).
const KPI_TOOLTIPS = {
  cpc: {
    title: 'Cost Per Click',
    desc: 'Average paid each time someone clicks a Doorly ad.',
    benchmark: 'Industry: $2–6 for mortgage keywords. Doorly target: under $4.',
    lowerIsBetter: true,
  },
  conversions: {
    title: 'Conversions',
    desc: 'Borrowers who submitted a full application after clicking a Doorly ad.',
    benchmark: 'Industry conversion rate: 2–5% of clicks. Doorly target: 3%+.',
    lowerIsBetter: false,
  },
  spend: {
    title: 'Total Spend',
    desc: 'Total ad spend across all selected campaigns in this period.',
    benchmark: 'Budget-dependent. Monitor CPA and CAC to evaluate efficiency.',
    lowerIsBetter: false,
  },
  cpa: {
    title: 'Cost Per Application',
    desc: 'Total spend divided by applications submitted. Measures ad efficiency.',
    benchmark: 'Industry: $150–400 per mortgage application. Doorly target: under $350.',
    lowerIsBetter: true,
  },
  cac: {
    title: 'Client Acquisition Cost',
    desc: 'Total ad spend divided by loans closed in the same window. Note: unattributed — includes loans from any source.',
    benchmark: 'Industry: $1,000–3,000 per closed loan. Doorly target: under $1,500.',
    lowerIsBetter: true,
  },
};

const REC_TYPE_LABELS = {
  bid_increase:    'Bid Increase',
  bid_decrease:    'Bid Decrease',
  negative_keyword:'Negative Keyword',
  new_ad_variant:  'New Ad Variant',
  budget_increase: 'Budget Reallocation',
  pause_keyword:   'Pause Keyword',
};

const REC_TYPE_COLORS = {
  bid_increase:    '#dcfce7',
  bid_decrease:    '#fef9c3',
  negative_keyword:'#fce7f3',
  new_ad_variant:  '#eff6ff',
  budget_increase: '#f3e8ff',
  pause_keyword:   '#fef2f2',
};

function fmt(cents) {
  return '$' + (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtMicros(micros) {
  return '$' + (micros / 1_000_000).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(val) {
  return (parseFloat(val) * 100).toFixed(1) + '%';
}

// Micros (Google Ads' 1M-units-per-dollar) → "$1,234". Returns em-dash on
// non-positive values so empty-period cards don't show "$0".
function fmtMicrosWhole(m) {
  return m > 0
    ? '$' + (m / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '—';
}
function fmtMicros2(m) {
  return m > 0 ? '$' + (m / 1_000_000).toFixed(2) : '—';
}

// Tooltip + DeltaBadge — defined as top-level components so they each own
// their own hook state (inline functions inside .map would violate Rules
// of Hooks if hooks were used).
function KpiTooltip({ config }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', cursor: 'help', marginLeft: 4 }}>
        ⓘ
      </span>
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%',
          transform: 'translateX(-50%)',
          background: '#1f2937', color: '#fff',
          borderRadius: 8, padding: '10px 12px',
          width: 220, zIndex: 100,
          marginBottom: 6, fontSize: 11, lineHeight: 1.5,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 12 }}>
            {config.title}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 6 }}>
            {config.desc}
          </div>
          <div style={{
            color: '#acf44e', fontSize: 10,
            borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6,
          }}>
            {config.benchmark}
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaBadge({ delta, lowerIsBetter }) {
  if (delta === null || delta === undefined) return null;
  const isFlat = Math.abs(delta) <= 5;
  const isPositive = lowerIsBetter ? delta < 0 : delta > 0;
  const color = isFlat
    ? 'rgba(255,255,255,0.4)'
    : isPositive ? '#acf44e' : '#ff082f';
  const arrow = isFlat ? '→' : delta > 0 ? '↑' : '↓';
  return (
    <div style={{ fontSize: 10, color, marginTop: 2, fontWeight: 600 }}>
      {arrow}{Math.abs(delta)}% vs last period
    </div>
  );
}

export default function AdminAdCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [processing, setProcessing] = useState({});
  // Google Ads campaign-builder state — connection status, seed preview,
  // and per-action busy flags so buttons can disable while in flight.
  const [gadsStatus, setGadsStatus] = useState(null);
  const [campaignPreview, setCampaignPreview] = useState([]);
  const [creating, setCreating]     = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [generating, setGenerating] = useState(false);
  // Platform sub-tab + KPI dashboard state.
  const [platform, setPlatform] = useState('google');
  const [kpis, setKpis]         = useState(null);
  const [kpiDays, setKpiDays]   = useState('30');
  const [kpiGeo, setKpiGeo]     = useState('all');
  const [kpiType, setKpiType]   = useState('all');

  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      // Existing reads from the agents.js read-only endpoints + new google-ads
      // status/preview reads. Promise.allSettled so a single failure (e.g. the
      // Google Ads status endpoint while env vars aren't set yet) doesn't
      // blank the whole page.
      const [cRes, rRes, sRes, pRes] = await Promise.allSettled([
        axios.get(`${API}/api/admin/agents/ads/campaigns`,        { headers }),
        axios.get(`${API}/api/admin/agents/ads/recommendations`,  { headers }),
        axios.get(`${API}/api/admin/google-ads/status`,           { headers }),
        axios.get(`${API}/api/admin/google-ads/campaign-preview`, { headers }),
      ]);
      if (cRes.status === 'fulfilled') setCampaigns(cRes.value.data || []);
      if (rRes.status === 'fulfilled') setRecommendations(rRes.value.data || []);
      if (sRes.status === 'fulfilled') setGadsStatus(sRes.value.data);
      if (pRes.status === 'fulfilled') setCampaignPreview(pRes.value.data?.campaigns || []);
    } catch (err) {
      console.error('Failed to load ads data:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pull KPIs separately — refetches when filters change. Always called with
  // explicit args so the React-stale-closure issue doesn't bite when the
  // user clicks two pills in quick succession.
  const fetchKpis = async (days, geo, type) => {
    try {
      const params = new URLSearchParams({
        days: days || kpiDays,
        geo:  geo  || kpiGeo,
        campaign_type: type || kpiType,
      });
      const res = await axios.get(`${API}/api/admin/agents/ads/kpis?${params}`, { headers });
      setKpis(res.data);
    } catch (err) {
      console.error('KPI fetch failed:', err.message);
    }
  };

  useEffect(() => {
    fetchData();
    fetchKpis('30', 'all', 'all');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRec = async (id, action, reason) => {
    setProcessing(p => ({ ...p, [id]: true }));
    try {
      await axios.patch(
        `${API}/api/admin/agents/ads/recommendations/${id}/${action}`,
        action === 'reject' ? { reason } : {},
        { headers }
      );
      await fetchData();
    } catch (err) {
      console.error(err.message);
    } finally {
      setProcessing(p => ({ ...p, [id]: false }));
    }
  };

  // Totals
  const totalBudget = campaigns.reduce(
    (s, c) => s + (c.budget_monthly_cents || 0), 0);
  const totalImpressions = campaigns.reduce(
    (s, c) => s + (c.latest_snapshot?.impressions || 0), 0);
  const totalClicks = campaigns.reduce(
    (s, c) => s + (c.latest_snapshot?.clicks || 0), 0);
  const totalConversions = campaigns.reduce(
    (s, c) => s + (c.latest_snapshot?.conversions || 0), 0);
  const totalSpend = campaigns.reduce(
    (s, c) => s + (c.latest_snapshot?.cost_micros || 0), 0);
  const pendingRecs = recommendations.filter(
    r => r.status === 'pending_review').length;

  const tabStyle = (tab) => ({
    padding: '8px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: activeTab === tab ? '#3a6ad9' : 'transparent',
    color: activeTab === tab ? '#fff' : '#6b7280',
  });

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', marginBottom: 24,
        }}>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 2 }}>
              Marketing
            </div>
            <h1 style={{
              fontSize: 22, fontWeight: 700,
              color: '#111827', margin: 0,
            }}>
              Paid Ads
            </h1>
          </div>
          {pendingRecs > 0 && (
            <div style={{
              background: '#fef9c3',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 12,
              color: '#92400e',
              fontWeight: 600,
            }}>
              {pendingRecs} recommendation{pendingRecs !== 1 ? 's' : ''} pending review
            </div>
          )}
        </div>

        {/* Platform sub-tabs — Google live, Meta + LinkedIn scaffolded. */}
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: '2px solid #e5e7eb',
          marginBottom: 20,
        }}>
          {PLATFORMS.map(p => (
            <button key={p.value}
              onClick={() => !p.comingSoon && setPlatform(p.value)}
              style={{
                padding: '10px 20px',
                border: 'none', background: 'none',
                fontSize: 13, fontWeight: 600,
                color: platform === p.value ? '#111827' : '#9ca3af',
                borderBottom: platform === p.value
                  ? '2px solid #111827'
                  : '2px solid transparent',
                marginBottom: -2,
                cursor: p.comingSoon ? 'default' : 'pointer',
              }}>
              {p.label}
              {p.comingSoon && (
                <span style={{
                  fontSize: 9, background: '#f3f4f6',
                  color: '#9ca3af', borderRadius: 4,
                  padding: '1px 5px', marginLeft: 6,
                  fontWeight: 500,
                }}>
                  Soon
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Coming-soon placeholder for non-Google platforms. */}
        {platform !== 'google' && (
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 10, padding: 40, textAlign: 'center',
            color: '#6b7280', fontSize: 13,
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
              {PLATFORMS.find(p => p.value === platform)?.label} — coming soon
            </div>
            <div>This platform isn't wired into the dashboard yet.</div>
          </div>
        )}

        {/* ── KPI dashboard (Google Ads platform only) ─────────────────────
            Filter pills above a dark-blue 5-card strip. Each card shows
            absolute value + vs-last-period delta. Tooltips on the ⓘ glyph
            carry plain-English copy + industry benchmark. */}
        {platform === 'google' && kpis && (
          <div>
            <div style={{
              display: 'flex', gap: 8, marginBottom: 12,
              flexWrap: 'wrap', alignItems: 'center',
            }}>
              {[
                { value: '7',   label: 'Last 7 days' },
                { value: '30',  label: 'Last 30 days' },
                { value: '90',  label: 'Last 90 days' },
                { value: 'all', label: 'All time' },
              ].map(d => (
                <button key={d.value}
                  onClick={() => { setKpiDays(d.value); fetchKpis(d.value, kpiGeo, kpiType); }}
                  style={{
                    padding: '5px 12px', borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: kpiDays === d.value ? '#111827' : '#fff',
                    color: kpiDays === d.value ? '#fff' : '#374151',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {d.label}
                </button>
              ))}

              <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px' }} />

              {['all', 'Houston', 'Austin'].map(g => (
                <button key={g}
                  onClick={() => { setKpiGeo(g); fetchKpis(kpiDays, g, kpiType); }}
                  style={{
                    padding: '5px 12px', borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: kpiGeo === g ? '#3a6ad9' : '#fff',
                    color: kpiGeo === g ? '#fff' : '#374151',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {g === 'all' ? 'All Cities' : g}
                </button>
              ))}

              <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px' }} />

              {[
                { value: 'all',         label: 'All Types' },
                { value: 'High Intent', label: 'High Intent' },
                { value: 'Alternative', label: 'Alternative' },
              ].map(t => (
                <button key={t.value}
                  onClick={() => { setKpiType(t.value); fetchKpis(kpiDays, kpiGeo, t.value); }}
                  style={{
                    padding: '5px 12px', borderRadius: 999,
                    border: '1px solid #e5e7eb',
                    background: kpiType === t.value ? '#3a6ad9' : '#fff',
                    color: kpiType === t.value ? '#fff' : '#374151',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            <div style={{
              background: '#3a6ad9',
              borderRadius: 14,
              padding: '20px 24px',
              marginBottom: 20,
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 0,
            }}>
              {(() => {
                const c = kpis.current;
                const d = kpis.deltas;
                const cards = [
                  { key: 'cpc',         label: 'CPC',         value: fmtMicros2(c.cpc_micros),     delta: d.cpc,         tooltip: KPI_TOOLTIPS.cpc },
                  { key: 'conversions', label: 'Conversions', value: c.conversions.toLocaleString(), delta: d.conversions, tooltip: KPI_TOOLTIPS.conversions },
                  { key: 'spend',       label: 'Spend',       value: fmtMicrosWhole(c.spend_micros), delta: d.spend,       tooltip: KPI_TOOLTIPS.spend },
                  { key: 'cpa',         label: 'CPA',         value: c.cpa_micros > 0 ? '$' + (c.cpa_micros / 1_000_000).toFixed(0) : '—', delta: d.cpa, tooltip: KPI_TOOLTIPS.cpa },
                  { key: 'cac',         label: 'CAC',         value: c.cac_micros   ? '$' + (c.cac_micros   / 1_000_000).toFixed(0) : '—', delta: d.cac, tooltip: KPI_TOOLTIPS.cac },
                ];
                return cards.map((card, i) => (
                  <div key={card.key} style={{
                    padding: '0 20px',
                    borderRight: i < cards.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      fontSize: 10, fontWeight: 700,
                      color: 'rgba(255,255,255,0.6)',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      marginBottom: 8,
                    }}>
                      {card.label}
                      <KpiTooltip config={card.tooltip} />
                    </div>
                    <div style={{
                      fontSize: 26, fontWeight: 800,
                      color: '#fff', marginBottom: 4,
                      fontFamily: 'monospace',
                    }}>
                      {card.value}
                    </div>
                    <DeltaBadge delta={card.delta} lowerIsBetter={card.tooltip.lowerIsBetter} />
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Summary cards — Google-only. Per-platform summaries will live
            under their own platform branches once Meta/LinkedIn are wired. */}
        {platform === 'google' && !loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 12,
            marginBottom: 24,
          }}>
            {[
              { label: 'Monthly Budget', value: fmt(totalBudget) },
              { label: 'Impressions', value: totalImpressions.toLocaleString() },
              { label: 'Clicks', value: totalClicks.toLocaleString() },
              { label: 'Applications', value: totalConversions.toLocaleString() },
              { label: 'Spend (Last Snapshot)', value: fmtMicros(totalSpend) },
            ].map(stat => (
              <div key={stat.label} style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{
                  fontSize: 11, color: '#6b7280',
                  fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', marginBottom: 4,
                }}>
                  {stat.label}
                </div>
                <div style={{
                  fontSize: 20, fontWeight: 700, color: '#111827',
                }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Google Ads connection banner + action bar — only renders once the
            /status probe has returned, and only on the Google platform tab.
            Sync + Generate buttons are enabled regardless of connection
            state so the API surfaces its own error, but they will of course
            no-op until env vars are set on Render. */}
        {platform === 'google' && gadsStatus && (
          <div style={{
            background: gadsStatus.connected ? '#f0fdf4' : '#fef9c3',
            border: `1px solid ${gadsStatus.connected ? '#86efac' : '#fde68a'}`,
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div style={{
              fontSize: 13, fontWeight: 600,
              color: gadsStatus.connected ? '#166534' : '#854d0e',
            }}>
              {gadsStatus.connected
                ? `Google Ads connected — Customer ${gadsStatus.customer_id}`
                : `Google Ads not connected${gadsStatus.error ? ` — ${gadsStatus.error}` : ''}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={syncing}
                onClick={async () => {
                  setSyncing(true);
                  try {
                    await axios.post(`${API}/api/admin/google-ads/sync`, {}, { headers });
                    await fetchData();
                  } catch (err) {
                    alert(err.response?.data?.error || err.message);
                  } finally {
                    setSyncing(false);
                  }
                }}
                style={{
                  background: syncing ? '#9ca3af' : '#fff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8, padding: '6px 14px',
                  fontSize: 12, fontWeight: 600,
                  cursor: syncing ? 'default' : 'pointer',
                }}>
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
              <button disabled={generating}
                onClick={async () => {
                  setGenerating(true);
                  try {
                    await axios.post(`${API}/api/admin/google-ads/generate-recommendations`, {}, { headers });
                    await fetchData();
                  } catch (err) {
                    alert(err.response?.data?.error || err.message);
                  } finally {
                    setGenerating(false);
                  }
                }}
                style={{
                  background: generating ? '#9ca3af' : '#3a6ad9',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8, padding: '6px 14px',
                  fontSize: 12, fontWeight: 600,
                  cursor: generating ? 'default' : 'pointer',
                }}>
                {generating ? 'Analyzing...' : 'Generate Recommendations'}
              </button>
            </div>
          </div>
        )}

        {/* Sub-tabs (within Google platform): Campaigns / Recommendations / Setup. */}
        {platform === 'google' && (
        <div style={{
          display: 'flex', gap: 4,
          background: '#f3f4f6',
          borderRadius: 10,
          padding: 4,
          width: 'fit-content',
          marginBottom: 20,
        }}>
          <button style={tabStyle('campaigns')}
            onClick={() => setActiveTab('campaigns')}>
            Campaigns
          </button>
          <button style={tabStyle('recommendations')}
            onClick={() => setActiveTab('recommendations')}>
            Recommendations
            {pendingRecs > 0 && (
              <span style={{
                marginLeft: 6,
                background: '#ff082f',
                color: '#fff',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 6px',
              }}>
                {pendingRecs}
              </span>
            )}
          </button>
          {/* Setup tab — surfaces only while no campaigns have been pushed to
              Google Ads yet (all rows have null google_campaign_id, or there
              are no campaigns at all). */}
          {campaigns.every(c => !c.google_campaign_id) && (
            <button style={tabStyle('setup')}
              onClick={() => setActiveTab('setup')}>
              Campaign Setup
            </button>
          )}
        </div>
        )}

        {platform === 'google' && loading && (
          <div style={{ color: '#6b7280', fontSize: 13, padding: 20 }}>
            Loading...
          </div>
        )}

        {/* Campaigns tab */}
        {platform === 'google' && !loading && activeTab === 'campaigns' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {campaigns.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '32px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: 13,
              }}>
                No campaigns yet — Google Ads API connection pending
              </div>
            )}
            {campaigns.map(c => {
              const s = c.latest_snapshot || {};
              const sc = STATUS_COLORS[c.status] || STATUS_COLORS.paused;
              return (
                <div key={c.id} style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '18px 22px',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 14,
                  }}>
                    <div>
                      <div style={{
                        fontSize: 15, fontWeight: 700,
                        color: '#111827', marginBottom: 2,
                      }}>
                        {c.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {c.geo} &nbsp;·&nbsp; Budget: {fmt(c.budget_monthly_cents)}/mo
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600,
                      background: sc.bg, color: sc.color,
                      borderRadius: 999, padding: '3px 10px',
                    }}>
                      {sc.label}
                    </span>
                  </div>

                  {s.impressions !== undefined ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      gap: 12,
                    }}>
                      {[
                        { label: 'Impressions', value: s.impressions?.toLocaleString() ?? '—' },
                        { label: 'Clicks', value: s.clicks?.toLocaleString() ?? '—' },
                        { label: 'CTR', value: s.ctr ? fmtPct(s.ctr) : '—' },
                        { label: 'Applications', value: s.conversions?.toLocaleString() ?? '—' },
                        // CPA = 0 means no conversions yet — render em-dash, not "$0".
                        { label: 'CPA', value: s.cpa_cents > 0 ? fmt(s.cpa_cents) : '—' },
                      ].map(m => (
                        <div key={m.label} style={{
                          background: '#f9fafb',
                          borderRadius: 8,
                          padding: '10px 12px',
                        }}>
                          <div style={{
                            fontSize: 10, color: '#9ca3af',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 3,
                          }}>
                            {m.label}
                          </div>
                          <div style={{
                            fontSize: 16, fontWeight: 700,
                            color: '#111827',
                          }}>
                            {m.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      fontSize: 12, color: '#9ca3af',
                      fontStyle: 'italic',
                    }}>
                      No performance data yet
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Recommendations tab */}
        {platform === 'google' && !loading && activeTab === 'recommendations' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {recommendations.length === 0 && (
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '32px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: 13,
              }}>
                No pending recommendations
              </div>
            )}
            {recommendations.map(r => {
              const tagBg = REC_TYPE_COLORS[r.recommendation_type] || '#f3f4f6';
              return (
                <div key={r.id} style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: '18px 22px',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 10,
                  }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: tagBg,
                        color: '#374151',
                        borderRadius: 6,
                        padding: '3px 10px',
                      }}>
                        {REC_TYPE_LABELS[r.recommendation_type] || r.recommendation_type}
                      </span>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>
                        {r.campaign_name}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    fontSize: 13, fontWeight: 600,
                    color: '#111827', marginBottom: 8,
                  }}>
                    {r.description}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: 10, marginBottom: 14,
                  }}>
                    {[
                      { label: 'Current', value: r.current_value },
                      { label: 'Recommended', value: r.recommended_value },
                      { label: 'Est. Impact', value: r.estimated_impact },
                    ].map(f => f.value && (
                      <div key={f.label} style={{
                        background: '#f9fafb',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}>
                        <div style={{
                          fontSize: 10, color: '#9ca3af',
                          fontWeight: 600, textTransform: 'uppercase',
                          letterSpacing: '0.05em', marginBottom: 2,
                        }}>
                          {f.label}
                        </div>
                        <div style={{
                          fontSize: 12, color: '#374151', fontWeight: 500,
                        }}>
                          {f.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      disabled={processing[r.id]}
                      onClick={() => handleRec(r.id, 'approve')}
                      style={{
                        background: processing[r.id] ? '#9ca3af' : '#3a6ad9',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 7,
                        padding: '7px 16px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: processing[r.id] ? 'default' : 'pointer',
                      }}>
                      Approve
                    </button>
                    <button
                      disabled={processing[r.id]}
                      onClick={() => {
                        const reason = window.prompt('Reason for rejection (optional):');
                        if (reason !== null) handleRec(r.id, 'reject', reason);
                      }}
                      style={{
                        background: '#fff',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: 7,
                        padding: '7px 16px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: processing[r.id] ? 'default' : 'pointer',
                      }}>
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Setup tab — preview + one-click create ─────────────────────── */}
        {platform === 'google' && !loading && activeTab === 'setup' && (
          <div>
            <div style={{
              background: '#fef9c3',
              border: '1px solid #fde68a',
              borderRadius: 10, padding: '16px 20px',
              marginBottom: 20, fontSize: 13,
              color: '#854d0e', lineHeight: 1.6,
            }}>
              <strong>Campaigns have not been created in Google Ads yet.</strong>
              Review the structure below and click <em>Create All Campaigns</em>
              to launch them in PAUSED state. Activate them manually in Google Ads
              after a final review.
            </div>

            {campaignPreview.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 12, padding: 16 }}>
                Loading preview…
              </div>
            )}

            {campaignPreview.map((c, i) => (
              <div key={i} style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 12, padding: '18px 22px',
                marginBottom: 12,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', marginBottom: 6,
                }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: '#111827',
                  }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    ${c.budget_monthly}/mo · ${c.budget_daily.toFixed(2)}/day
                  </div>
                </div>
                <div style={{
                  fontSize: 12, color: '#6b7280', marginBottom: 10,
                }}>
                  {c.geo} · {c.keyword_count} keywords ·
                  {' '}{c.negative_keyword_count} negatives · {c.ad_count} ads
                </div>
                <div style={{
                  display: 'flex', gap: 6, flexWrap: 'wrap',
                }}>
                  {c.keywords?.slice(0, 5).map((kw, ki) => (
                    <span key={ki} style={{
                      fontSize: 10, background: '#eff6ff',
                      color: '#1e40af', borderRadius: 4,
                      padding: '2px 6px',
                    }}>
                      {kw.text}
                    </span>
                  ))}
                  {c.keywords?.length > 5 && (
                    <span style={{ fontSize: 10, color: '#9ca3af', padding: '2px 0' }}>
                      +{c.keywords.length - 5} more
                    </span>
                  )}
                </div>

                {/* Negative keywords — red-tinted pills with a leading minus
                    so they're visually distinct from positive keywords. */}
                {c.negative_keywords && c.negative_keywords.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
                    }}>
                      Negative Keywords ({c.negative_keywords.length})
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {c.negative_keywords.map((kw, ki) => (
                        <span key={ki} style={{
                          fontSize: 10, background: '#fef2f2', color: '#991b1b',
                          borderRadius: 4, padding: '2px 6px',
                        }}>
                          −{kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ad Copy preview — mimics the Google SERP rendering: blue
                    headline, green display URL, gray description. */}
                {c.ads && c.ads.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: '#6b7280',
                      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
                    }}>
                      Ad Copy ({c.ads.length} variant{c.ads.length !== 1 ? 's' : ''})
                    </div>
                    {c.ads.map((ad, ai) => (
                      <div key={ai} style={{
                        background: '#f9fafb', border: '1px solid #e5e7eb',
                        borderRadius: 8, padding: '10px 12px', marginBottom: 8,
                      }}>
                        <div style={{ fontSize: 10, color: '#1a0dab', fontWeight: 600, marginBottom: 2 }}>
                          {ad.headline_1} | {ad.headline_2} | {ad.headline_3}
                        </div>
                        <div style={{ fontSize: 10, color: '#006621', marginBottom: 3 }}>
                          app.godoorly.com
                        </div>
                        <div style={{ fontSize: 10, color: '#545454' }}>
                          {ad.description_1}
                        </div>
                        <div style={{ fontSize: 10, color: '#545454' }}>
                          {ad.description_2}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {campaignPreview.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button
                  disabled={creating}
                  onClick={async () => {
                    if (!window.confirm(
                      'Create all 3 campaigns in Google Ads? They will be created in PAUSED state.'
                    )) return;
                    setCreating(true);
                    try {
                      const res = await axios.post(
                        `${API}/api/admin/google-ads/create-campaigns`,
                        {}, { headers }
                      );
                      const failed = res.data.results?.filter(r => r.status === 'error');
                      if (failed?.length > 0) {
                        alert(`Some campaigns failed: ${failed.map(f => `${f.name}: ${f.error}`).join('\n')}`);
                      } else {
                        alert('All campaigns created successfully! They are paused — activate in Google Ads when ready.');
                      }
                      await fetchData();
                      setActiveTab('campaigns');
                    } catch (err) {
                      alert(err.response?.data?.error || err.message);
                    } finally {
                      setCreating(false);
                    }
                  }}
                  style={{
                    background: creating ? '#9ca3af' : '#acf44e',
                    color: '#111827',
                    border: 'none', borderRadius: 10,
                    padding: '12px 28px',
                    fontSize: 14, fontWeight: 700,
                    cursor: creating ? 'default' : 'pointer',
                  }}>
                  {creating ? 'Creating Campaigns...' : 'Create All Campaigns in Google Ads →'}
                </button>
                <div style={{
                  fontSize: 11, color: '#9ca3af', marginTop: 8,
                }}>
                  Campaigns will be created in PAUSED state. Review in Google Ads before activating.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
